import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Ticket as TicketIcon,
  Activity,
  AlertTriangle,
  Clock,
  ChevronRight,
  Download,
  type LucideProps,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
  Legend,
} from "recharts";
import { useState, type ComponentType, type ReactNode } from "react";
import { api, type Paginated, type Ticket as TicketType } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard } from "../components/ui/GlassCard";
import { SpotlightCard } from "../components/ui/SpotlightCard";
import { PriorityBadge } from "../components/tickets/PriorityBadge";
import { StatusBadge } from "../components/tickets/StatusBadge";
import { SLACountdown } from "../components/tickets/SLACountdown";
import { Avatar } from "../components/ui/Avatar";
import { fmtRelative } from "../lib/format";
import { PRIORITY_META, STATUS_META } from "../lib/constants";
import { EmptyState } from "../components/ui/EmptyState";

type Accent = "brand" | "critical" | "amber" | "emerald";

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  accent = "brand",
}: {
  icon: ComponentType<LucideProps>;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  accent?: Accent;
}) {
  const accents: Record<Accent, string> = {
    brand: "from-brand-400/30 to-brand-600/5 text-brand-200",
    critical: "from-critical-400/30 to-critical-500/5 text-critical-200",
    amber: "from-amber-400/30 to-amber-500/5 text-amber-200",
    emerald: "from-emerald-400/30 to-emerald-500/5 text-emerald-200",
  };
  const spotlight: Record<Accent, string> = {
    brand: "rgba(0, 86, 255, 0.25)",
    critical: "rgba(251, 11, 20, 0.25)",
    amber: "rgba(245, 158, 11, 0.25)",
    emerald: "rgba(16, 185, 129, 0.25)",
  };
  return (
    <SpotlightCard spotlightColor={spotlight[accent]} className="h-full flex flex-col justify-center">
      <div
        className={`absolute inset-0 bg-gradient-to-br ${accents[accent]} opacity-60 pointer-events-none`}
      />
      <div className="relative flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-slate-400">
            {label}
          </div>
          <div className="text-3xl font-bold text-white leading-tight">{value}</div>
          {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
        </div>
      </div>
    </SpotlightCard>
  );
}

const tooltipStyle = {
  background: "rgba(20,20,40,0.9)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: "12px",
  color: "#fff",
  fontSize: "12px",
};

export default function Dashboard() {
  const { user, isStaff } = useAuth();
  const [priorityFilter, setPriorityFilter] = useState("ALL");
  const [trendMonth, setTrendMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [isAllDates, setIsAllDates] = useState(false);
  const currentMonthParam = isAllDates ? "ALL" : trendMonth;

  const [progressTab, setProgressTab] = useState<"monthly" | "weekly" | "daily">("monthly");
  const [dailyDate, setDailyDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });

  const getWeeklyData = (items: any[]) => {
    if (!items) return [];

    return items.filter((item) => {
      const [y, m, dayOfMonth] = item.date.split('-');
      const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(dayOfMonth, 10));
      const day = d.getDay();
      return day >= 1 && day <= 5; // Monday to Friday
    }).map((item) => {
      const [, m, dayOfMonth] = item.date.split('-');
      return {
        ...item,
        date: `${parseInt(m, 10)}-${parseInt(dayOfMonth, 10)}`
      };
    });
  };
  const last5Days = Array.from({ length: 5 }).map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const label = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    return { value, label };
  });

  const [chartTab, setChartTab] = useState<"priority" | "status" | "imr">("priority");

  // Recent tickets (mine for user, all for staff)
  const recent = useQuery({
    queryKey: ["dashboard.recent", user?.id, isStaff],
    queryFn: async () => {
      const params = {
        pageSize: 20,
        sort: "createdAt",
        order: "desc",
        ...(isStaff ? {} : { mine: true }),
      };
      const { data } = await api.get<Paginated<TicketType>>("/api/tickets", {
        params,
      });
      return data;
    },
    refetchInterval: 60_000, // Poll every 60s so new tickets appear without refresh
  });

  // At-risk + breached
  const atRisk = useQuery({
    queryKey: ["dashboard.atRisk", user?.id, isStaff],
    queryFn: async () => {
      const params = {
        slaState: ["at-risk", "breached"],
        status: ["OPEN", "IN_PROGRESS", "ON_HOLD"],
        pageSize: 20,
        ...(isStaff ? {} : { mine: true }),
      };
      const { data } = await api.get<Paginated<TicketType>>("/api/tickets", {
        params,
      });
      return data;
    },
    refetchInterval: 300_000, // 5 minutes (Hostinger DB connection limit protection)
  });

  // Staff-only: aggregate reports
  const summary = useQuery<any>({
    queryKey: ["report.summary", currentMonthParam],
    queryFn: async () => (await api.get("/api/reports/summary", { params: { month: currentMonthParam } })).data,
    enabled: isStaff,
    refetchInterval: 15_000,
  });
  const sla = useQuery<any>({
    queryKey: ["report.sla", currentMonthParam],
    queryFn: async () => (await api.get("/api/reports/sla", { params: { month: currentMonthParam } })).data,
    enabled: isStaff,
    refetchInterval: 15_000,
  });

  const dailySummary = useQuery<any>({
    queryKey: ["report.summary", dailyDate],
    queryFn: async () => (await api.get("/api/reports/summary", { params: { month: dailyDate } })).data,
    enabled: isStaff,
    refetchInterval: 15_000,
  });
  const dailySla = useQuery<any>({
    queryKey: ["report.sla", dailyDate],
    queryFn: async () => (await api.get("/api/reports/sla", { params: { month: dailyDate } })).data,
    enabled: isStaff,
    refetchInterval: 15_000,
  });

  const trend = useQuery<any>({
    queryKey: ["report.trend", currentMonthParam],
    queryFn: async () => (await api.get("/api/reports/trend", { params: { month: currentMonthParam } })).data,
    enabled: isStaff,
    refetchInterval: 15_000,
  });

  const agents = useQuery<any[]>({
    queryKey: ["report.agents", currentMonthParam],
    queryFn: async () => (await api.get("/api/reports/agents", { params: { month: currentMonthParam } })).data.items,
    enabled: isStaff,
    refetchInterval: 15_000,
  });

  const imrData = (agents.data || [])
    .filter((a: any) => (a.role === "IT" || a.role === "ADMIN") && a.resolvedCount > 0)
    .sort((a: any, b: any) => b.resolvedCount - a.resolvedCount)
    .slice(0, 5)
    .map((a: any) => ({
      name: a.name,
      resolvedCount: a.resolvedCount,
      onTimeRate: a.resolvedCount > 0 ? Math.max(0, (a.resolvedCount - a.breachedCount) / a.resolvedCount) : null,
      avgHours: a.avgResolutionHours,
    }));

  // For non-staff users, derive stats locally from their tickets
  const userStats = (() => {
    if (isStaff || !recent.data) return null;
    const all = recent.data.items;
    const open = all.filter((t) =>
      ["OPEN", "IN_PROGRESS", "ON_HOLD"].includes(t.status),
    ).length;
    const resolved = all.filter((t) =>
      ["RESOLVED", "CLOSED"].includes(t.status),
    ).length;
    const atRiskN = atRisk.data?.items?.length || 0;
    return { total: recent.data.total, open, resolved, atRiskN };
  })();

  const stats: any = isStaff
    ? {
      total: summary.data?.totals?.total ?? "—",
      open: summary.data?.totals?.open ?? "—",
      resolved: summary.data?.totals?.resolved ?? "—",
      breached: sla.data?.openBreached ?? "—",
      onTimeRate: sla.data?.onTimeRate,
      avg: sla.data?.avgResolutionHours,
    }
    : userStats || { total: "—", open: "—", resolved: "—", atRiskN: "—" };

  const currentSummaryData = progressTab === "daily" ? dailySummary.data : summary.data;

  const priorityData = (currentSummaryData?.byPriority || [])
    .filter((p: any) => priorityFilter === "ALL" || p.priority === priorityFilter)
    .map((p: any) => ({
      name: PRIORITY_META[p.priority as keyof typeof PRIORITY_META]?.label || p.priority,
      value: p.count,
      fill: PRIORITY_META[p.priority as keyof typeof PRIORITY_META]?.color || "#999",
    }));

  const statusData = (currentSummaryData?.byStatus || []).map((s: any) => ({
    name: s.status.replace("_", " "),
    count: s.count,
    fill: STATUS_META[s.status as keyof typeof STATUS_META]?.color || "#000eb5",
  }));

  const handleExportMonthlyProgress = () => {
    if (!trend.data?.items?.length) {
      import("sonner").then((m) => m.toast.error("No data to export"));
      return;
    }
    const headers = ["Date", "Created Tickets", "Resolved Tickets"];
    const rows = trend.data.items.map((row: any) => [
      `"${row.date}"`,
      row.created,
      row.resolved,
    ]);
    const csvContent = [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `monthly_progress_${trendMonth}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    import("sonner").then((m) => m.toast.success("Export successful"));
  };

  const handleExportWeeklyProgress = () => {
    const weeklyData = getWeeklyData(trend.data?.items);
    if (!weeklyData?.length) {
      import("sonner").then((m) => m.toast.error("No data to export"));
      return;
    }

    const headers = ["Date", "Created Tickets", "Resolved Tickets"];
    const rows = weeklyData.map((row: any) => [
      `"${row.date}"`,
      row.created,
      row.resolved,
    ]);
    const csvContent = [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `weekly_progress_${trendMonth}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    import("sonner").then((m) => m.toast.success("Export successful"));
  };

  const handleExportDailyProgress = () => {
    const headers = ["Date", "Open", "Resolved", "Breached", "Closed"];
    const rows = [
      [
        `"${dailyDate}"`,
        dailySummary.data?.totals?.open ?? 0,
        dailySla.data?.resolvedCount ?? 0,
        dailySla.data?.openBreached ?? 0,
        dailySummary.data?.byStatus?.find((s: any) => s.status === 'CLOSED')?.count ?? 0,
      ]
    ];
    const csvContent = [headers.join(","), ...rows.map((r: any) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `daily_progress_${dailyDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    import("sonner").then((m) => m.toast.success("Export successful"));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="page-title">Welcome back, {user?.name?.split(" ")[0]}</h1>
        <p className="page-subtitle">
          {isStaff
            ? "Operations overview, SLA health, and team activity."
            : "Your tickets at a glance."}
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard icon={TicketIcon} label="Total" value={stats.total} accent="brand" />
        <StatCard
          icon={Activity}
          label="Open"
          value={stats.open}
          accent="amber"
          hint={isStaff ? undefined : "Your active tickets"}
        />
        <StatCard
          icon={Clock}
          label={isStaff ? "Resolved" : "Closed"}
          value={stats.resolved}
          accent="emerald"
          hint={isStaff && stats.avg ? `Avg ${stats.avg}h to resolve` : undefined}
        />
        <StatCard
          icon={AlertTriangle}
          label={isStaff ? "Breached (open)" : "At risk"}
          value={isStaff ? stats.breached : stats.atRiskN}
          accent="critical"
          hint={
            isStaff && stats.onTimeRate !== null && stats.onTimeRate !== undefined
              ? `${(stats.onTimeRate * 100).toFixed(0)}% on-time`
              : undefined
          }
        />
      </div>

      {/* Charts row (staff only) */}
      {isStaff && (
        <div className="grid grid-cols-2 gap-4">
          <GlassCard className="col-span-1">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex bg-white/5 rounded-lg p-0.5 mb-2 w-max">
                  <button
                    onClick={() => setProgressTab("monthly")}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      progressTab === "monthly" ? "bg-white/10 text-white font-medium shadow-sm" : "text-slate-400 hover:text-slate-300"
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setProgressTab("weekly")}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      progressTab === "weekly" ? "bg-white/10 text-white font-medium shadow-sm" : "text-slate-400 hover:text-slate-300"
                    }`}
                  >
                    Weekly
                  </button>
                  <button
                    onClick={() => setProgressTab("daily")}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${
                      progressTab === "daily" ? "bg-white/10 text-white font-medium shadow-sm" : "text-slate-400 hover:text-slate-300"
                    }`}
                  >
                    Daily
                  </button>
                </div>
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  {progressTab === "monthly" ? "Monthly Progress" : progressTab === "weekly" ? "Weekly Progress" : "Daily Progress"}
                </h3>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {progressTab === "monthly" ? "Ticket creation and resolved" : progressTab === "weekly" ? "Every 5 days (Monday - Friday)" : `Activity for ${dailyDate}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {progressTab === "monthly" || progressTab === "weekly" ? (
                  <>
                    <button
                      onClick={() => setIsAllDates(!isAllDates)}
                      className={`text-xs h-7 px-3 rounded-md transition-all border select-none ${
                        isAllDates
                          ? "bg-brand-500/20 border-brand-500/50 text-brand-300 font-medium shadow-sm"
                          : "bg-white/5 border-white/10 text-slate-400 hover:text-slate-200 hover:bg-white/10"
                      }`}
                    >
                      All Dates
                    </button>
                    <input
                      type="month"
                      className={`w-auto text-xs py-1 h-7 px-2 bg-white/5 border border-white/10 rounded-md text-slate-300 focus:outline-none focus:ring-1 focus:ring-brand-500 transition-opacity ${isAllDates ? 'opacity-50 pointer-events-none' : ''}`}
                      value={trendMonth}
                      disabled={isAllDates}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) {
                          const d = new Date();
                          setTrendMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
                        } else {
                          setTrendMonth(val);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={progressTab === "monthly" ? handleExportMonthlyProgress : handleExportWeeklyProgress}
                      className="px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-slate-300 transition-colors"
                      title={progressTab === "monthly" ? "Export Monthly Data" : "Export Weekly Data"}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <select
                      className="w-auto text-xs py-1 h-7 px-2 bg-white/5 border border-white/10 rounded-md text-slate-300 focus:outline-none focus:ring-1 focus:ring-brand-500 transition-opacity"
                      value={dailyDate}
                      onChange={(e) => setDailyDate(e.target.value)}
                    >
                      {last5Days.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleExportDailyProgress}
                      className="px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-slate-300 transition-colors"
                      title="Export Daily Data"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {progressTab === "daily" ? (
              <div className="h-[260px] flex flex-col justify-center">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 to-transparent pointer-events-none" />
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 mb-1 z-10">Open</span>
                    <span className="text-3xl font-bold text-white z-10">{dailySummary.data?.totals?.open ?? "—"}</span>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 to-transparent pointer-events-none" />
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 mb-1 z-10">Resolved</span>
                    <span className="text-3xl font-bold text-white z-10">{dailySla.data?.resolvedCount ?? "—"}</span>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-critical-400/10 to-transparent pointer-events-none" />
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 mb-1 z-10">Breached</span>
                    <span className="text-3xl font-bold text-white z-10">{dailySla.data?.openBreached ?? "—"}</span>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-400/10 to-transparent pointer-events-none" />
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 mb-1 z-10">Closed</span>
                    <span className="text-3xl font-bold text-white z-10">
                      {dailySummary.data?.byStatus?.find((s: any) => s.status === 'CLOSED')?.count ?? 0}
                    </span>
                  </div>
                </div>
              </div>
            ) : trend.isLoading ? (
              <div className="h-[260px] grid place-items-center text-sm text-slate-400">Loading...</div>
            ) : !(progressTab === "weekly" ? getWeeklyData(trend.data?.items) : trend.data?.items)?.length ? (
              <div className="h-[260px] grid place-items-center text-sm text-slate-400">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={progressTab === "weekly" ? getWeeklyData(trend.data.items) : trend.data.items} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#818cf8" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34d399" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#94a3b8" 
                    fontSize={11} 
                    tickFormatter={(str) => {
                      if (progressTab === "weekly") return str;
                      if (str.length === 7) { // YYYY-MM
                         const [y, m] = str.split('-');
                         const d = new Date(parseInt(y), parseInt(m)-1);
                         return d.toLocaleString('default', { month: 'short', year: '2-digit' });
                      }
                      const d = new Date(str);
                      return `${d.getMonth() + 1}/${d.getDate()}`;
                    }}
                  />
                  <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} width={36} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Area type="monotone" dataKey="created" name="Created" stroke="#818cf8" fillOpacity={1} fill="url(#colorCreated)" />
                  <Area type="monotone" dataKey="resolved" name="Resolved" stroke="#34d399" fillOpacity={1} fill="url(#colorResolved)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </GlassCard>

          <GlassCard className="col-span-1 flex flex-col h-full">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="flex bg-white/5 rounded-lg p-0.5">
                <button
                  onClick={() => setChartTab("priority")}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    chartTab === "priority" ? "bg-white/10 text-white font-medium shadow-sm" : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  By Priority
                </button>
                <button
                  onClick={() => setChartTab("status")}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    chartTab === "status" ? "bg-white/10 text-white font-medium shadow-sm" : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  By Status
                </button>
                <button
                  onClick={() => setChartTab("imr")}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    chartTab === "imr" ? "bg-white/10 text-white font-medium shadow-sm" : "text-slate-400 hover:text-slate-300"
                  }`}
                  title="IT with Most Resolved"
                >
                  IMR
                </button>
              </div>
              {chartTab === "priority" && (
                <select
                  className="select w-auto text-xs py-1 h-7 pl-2 pr-6"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <option value="ALL">All</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              )}
            </div>

            <div className="flex-1 flex flex-col justify-center min-h-[220px]">
              {chartTab === "priority" ? (
                priorityData.length === 0 ? (
                  <div className="h-48 grid place-items-center text-sm text-slate-400">
                    No data
                  </div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie
                          data={priorityData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={40}
                          outerRadius={70}
                          paddingAngle={3}
                          stroke="rgba(0,0,0,0.3)"
                        >
                          {priorityData.map((d: any, i: number) => (
                            <Cell key={i} fill={d.fill} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                    <ul className="mt-6 grid grid-cols-2 gap-x-3 gap-y-2">
                      {priorityData.map((d: any) => (
                        <li
                          key={d.name}
                          className="flex items-center gap-2 text-xs text-slate-300 min-w-0"
                        >
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: d.fill }}
                          />
                          <span className="truncate flex-1 min-w-0">{d.name}</span>
                          <span className="ml-auto text-slate-400 tabular-nums shrink-0">
                            {d.value}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )
              ) : chartTab === "status" ? (
                statusData.length === 0 ? (
                  <div className="h-48 grid place-items-center text-sm text-slate-400">
                    No data
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={statusData}
                      margin={{ top: 5, right: 8, bottom: 0, left: -20 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(255,255,255,0.06)"
                      />
                      <XAxis
                        dataKey="name"
                        stroke="#94a3b8"
                        fontSize={11}
                        interval={0}
                        tickMargin={6}
                      />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={11}
                        allowDecimals={false}
                        width={36}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        cursor={{ fill: "rgba(255,255,255,0.04)" }}
                      />
                      <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                        {statusData.map((d: any, i: number) => (
                          <Cell key={`cell-${i}`} fill={d.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )
              ) : (
                imrData.length === 0 ? (
                  <div className="h-48 grid place-items-center text-sm text-slate-400">
                    No data
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto pr-2 space-y-3 mt-2 max-h-[240px]">
                    {imrData.map((d: any, i: number) => (
                      <div key={i} className="flex flex-col gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-white flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px] font-bold">#{i + 1}</span>
                            {d.name}
                          </span>
                          <span className="text-sm font-semibold text-emerald-400">{d.resolvedCount} <span className="text-[10px] font-normal text-slate-400 uppercase tracking-wider">Resolved</span></span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-white/5 rounded-lg p-2 flex flex-col">
                            <span className="text-[9px] uppercase tracking-wider text-slate-400">On-time Rate</span>
                            <span className="text-xs font-semibold text-slate-200">{d.onTimeRate !== null ? `${(d.onTimeRate * 100).toFixed(0)}%` : '—'}</span>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2 flex flex-col">
                            <span className="text-[9px] uppercase tracking-wider text-slate-400">Avg Resolution</span>
                            <span className="text-xs font-semibold text-slate-200">{d.avgHours !== null ? `${d.avgHours}h` : '—'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </GlassCard>
        </div>
      )}

      {/* Two-column lists */}
      <div className="grid grid-cols-3 gap-4">
        <GlassCard className="col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Recent Tickets</h3>
            <Link
              to="/tickets"
              className="text-xs text-brand-200 hover:text-white flex items-center gap-1"
            >
              View all <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          {recent.isLoading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton h-14" />
              ))}
            </div>
          ) : !recent.data?.items?.length ? (
            <EmptyState
              title="No tickets yet"
              description="Create your first ticket to get started."
              action={
                <Link to="/tickets?new=true" className="btn-primary inline-flex">
                  New Ticket
                </Link>
              }
            />
          ) : (
            <ul className="space-y-1 max-h-[380px] overflow-y-auto pr-2">
              {recent.data.items.map((t) => (
                <li key={t.id}>
                  <Link
                    to={`/tickets/${t.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">
                        {t.title}
                      </div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <PriorityBadge priority={t.priority} />
                        <StatusBadge status={t.status} />
                        <span className="text-[11px] text-slate-400">
                          · {fmtRelative(t.createdAt)}
                        </span>
                      </div>
                    </div>
                    <SLACountdown
                      deadline={t.slaDeadline}
                      priority={t.priority}
                      status={t.status}
                      compact
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </GlassCard>

        <GlassCard>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-300" /> SLA At Risk
            </h3>
            <Link
              to="/tickets?slaState=at-risk"
              className="text-xs text-brand-200 hover:text-white"
            >
              View
            </Link>
          </div>
          {atRisk.isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="skeleton h-12" />
              ))}
            </div>
          ) : !atRisk.data?.items?.length ? (
            <div className="text-sm text-slate-400 py-6 text-center">
              All tickets are on track 🎉
            </div>
          ) : (
            <ul className="space-y-1 max-h-[380px] overflow-y-auto pr-2">
              {atRisk.data.items.map((t) => (
                <li key={t.id}>
                  <Link
                    to={`/tickets/${t.id}`}
                    className="block p-3 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    <div className="text-sm text-white truncate">{t.title}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <PriorityBadge priority={t.priority} />
                      <SLACountdown
                        deadline={t.slaDeadline}
                        priority={t.priority}
                        status={t.status}
                        compact
                      />
                    </div>
                    {t.assignee && (
                      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400">
                        <Avatar name={t.assignee.name} url={t.assignee.avatarUrl} size="xs" /> {t.assignee.name}
                      </div>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
